// react
import { useState, useContext, ChangeEvent, useEffect } from 'react';

// types
import { Contract, Entity, Project, Transaction } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUser } from '../../../App.context';
import { ContextContracts } from '../../layouts/Providers/Providers.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Container, Grid, Skeleton, Typography } from '@mui/material';

// cards
import { CardTransaction } from '../../cards/CardTransaction/CardTransaction';

// dialogs
import { DialogTransaction } from '../../dialogs/DialogTransaction/DialogTransaction';

// ui
import { BannerProps } from '../Banner/Banner';

// fields
import { Select } from '../../fields/Select/Select';

// utils
import { formatDate } from '../../../utils/dates';

export const Transactions = () => {
	// session
	const token = sessionStorage.getItem('token');

	// hooks
	const { t } = useTranslation();

	// state
	const [filterValues, setFilterValues] = useState({ contract: 'all', entity: 'all' });
	const [isDialogTransactionOpen, setIsDialogTransactionOpen] = useState(false);
	const [transactions, setTransactions] = useState<Transaction[]>([]);
	const [selectedTransaction, setSelectedTransaction] = useState<Transaction>();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { contracts } = useContext(ContextContracts);
	const { user } = useContext(ContextUser);

	// vars
	const entityIds = (user?.entities as Entity[])?.map((e) => e.id).join(',');
	const idFormFilters = `form-transactions-filters`;

	let toEntity = entityIds;

	if (filterValues.entity && filterValues.entity !== 'all') {
		toEntity = filterValues.entity;
	}

	let transactionsUrl = `${process.env.REACT_APP_API_URL}/api/proxies/transactions?toEntity=${toEntity}&populate=benefit,contract,expenseType,fromEntity,project`;

	if (filterValues.contract && filterValues.contract !== 'all') {
		transactionsUrl += `&contract=${filterValues.contract}`;
	}

	const optionsContracts = contracts.map((contract: Contract) => ({
		label: contract.num,
		value: String(contract.id),
	}));

	const optionsEntities =
		(user?.entities as Entity[])?.map((entity) => {
			return {
				label: entity.name,
				value: String(entity.id),
			};
		}) || [];

	// fetch transactions
	const { fetchRequest: fetchTransactions, isLoading: isLoadingTransactions } = useFetch({
		isLazy: true,
		url: transactionsUrl,
		options: {
			headers: {
				Authorization: `Bearer ${token}`,
				apikey: process.env.REACT_APP_APIKEY || '',
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTransactions(res.data);
			}
		},
	});

	const handleUpload = async (e: ChangeEvent<HTMLInputElement>, response: any) => {
		const banner: BannerProps = {
			hasClose: true,
			message: t('alert.fileUploaded'),
			color: 'success',
		};

		if (response.error) {
			banner.color = 'error';
			banner.message = t('error.fileUploaded');
		}

		// set banner
		setBanner(banner);

		// close dialog
		setIsDialogTransactionOpen(false);
	};

	const handleConfirmationSuccess = () => {
		const banner: BannerProps = {
			hasClose: true,
			message: t('receiptConfirmationSuccess'),
			color: 'success',
		};

		// set banner
		setBanner(banner);

		// refetch transactions
		fetchTransactions();
	};

	const handleClickTransaction = (transaction: Transaction) => {
		setSelectedTransaction(transaction);
		setIsDialogTransactionOpen(true);
	};

	const renderContent = () => {
		if (isLoadingTransactions) {
			return (
				<Grid item xs={12}>
					<Grid container spacing={2}>
						{[...Array(3)].map((a, i) => {
							return (
								<Grid item key={`skeleton-${i}`} xs={12}>
									<Skeleton height={140} variant="rounded" />
								</Grid>
							);
						})}
					</Grid>
				</Grid>
			);
		}

		if (transactions.length) {
			return transactions.map((transaction) => {
				const project = transaction.project as Project | undefined;
				const isConfirmationType =
					transaction.type === 'CASH_BENEFIT' || transaction.type === 'PAYMENT';
				const dateConfirmationReceipt = project?.dateConfirmationReceipt
					? formatDate({
							date: new Date(project.dateConfirmationReceipt),
							format: 'YYYY-MM-DD',
						})
					: null;
				const dateTransaction = transaction.date
					? formatDate({
							date: new Date(transaction.date),
							format: 'YYYY-MM-DD',
						})
					: null;
				const hasReceiptConfirmation = Boolean(
					isConfirmationType &&
					dateConfirmationReceipt !== null &&
					dateTransaction !== null &&
					dateTransaction > dateConfirmationReceipt
				);

				return (
					<Grid item key={`transaction-${transaction.id}`} xs={12}>
						<CardTransaction
							hasReceiptConfirmation={hasReceiptConfirmation}
							onClick={() => handleClickTransaction(transaction)}
							onConfirmationSuccess={handleConfirmationSuccess}
							transaction={transaction}
						/>
					</Grid>
				);
			});
		}

		return (
			<Grid item xs={12}>
				<Alert severity="info" variant="filled">
					{t('alert.empty')}
				</Alert>
			</Grid>
		);
	};

	// fetch transactions when filterValues changes
	useEffect(() => {
		fetchTransactions();
	}, [fetchTransactions, filterValues.contract, toEntity]);

	return (
		<Container maxWidth={false} sx={{ color: 'common.white' }}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Typography color="common.white" variant="h2">
						{t('transactions')}
					</Typography>
				</Grid>
				<Grid item xs={12}>
					<form id={idFormFilters}>
						<Grid container spacing={2}>
							{optionsEntities.length > 1 && (
								<Grid item xs={12}>
									<Select
										hasAllOption={true}
										hasNoneOption={false}
										id={`${idFormFilters}-entity`}
										label={t('entity')}
										name="entity"
										onChange={(e) => {
											const { value } = e.target as HTMLInputElement;
											setFilterValues((prev) => ({ ...prev, entity: value }));
										}}
										options={optionsEntities}
										value={filterValues.entity}
									/>
								</Grid>
							)}
							{optionsContracts.length > 0 && (
								<Grid item xs={12}>
									<Select
										hasAllOption={true}
										hasNoneOption={false}
										id={`${idFormFilters}-contract`}
										label={t('contract')}
										name="contract"
										onChange={(e) => {
											const { value } = e.target as HTMLInputElement;
											setFilterValues((prev) => ({ ...prev, contract: value }));
										}}
										options={optionsContracts}
										value={filterValues.contract}
									/>
								</Grid>
							)}
						</Grid>
					</form>
				</Grid>

				{renderContent()}
			</Grid>
			<DialogTransaction
				isOpen={isDialogTransactionOpen}
				onClose={() => setIsDialogTransactionOpen(false)}
				onUpload={handleUpload}
				transaction={selectedTransaction}
			/>
		</Container>
	);
};
